import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  XMarkIcon, 
  DocumentTextIcon, 
  CheckCircleIcon, 
  ExclamationCircleIcon, 
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CalendarIcon,
  ServerIcon
} from '@heroicons/react/24/outline';

// Mock deployment history data
const mockDeployments = [
  {
    id: 'D43210',
    date: '2025-04-25 23:45:12',
    targets: ['production-lb1'],
    type: 'Configuration',
    status: 'success',
    user: 'admin@example.com'
  },
  {
    id: 'D43209',
    date: '2025-04-24 14:30:05',
    targets: ['staging-lb1'],
    type: 'Configuration',
    status: 'success',
    user: 'deploy@example.com'
  },
  {
    id: 'D43208',
    date: '2025-04-23 10:15:30',
    targets: ['production-lb1', 'staging-lb1', 'dev-lb1'],
    type: 'Certificate',
    status: 'partial',
    user: 'admin@example.com'
  },
  {
    id: 'D43207',
    date: '2025-04-22 09:00:15',
    targets: ['dev-lb1'],
    type: 'Configuration',
    status: 'failed',
    user: 'deploy@example.com'
  }
];

interface DeploymentHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DeploymentHistoryModal: React.FC<DeploymentHistoryModalProps> = ({ isOpen, onClose }) => {
  const [selectedDeployment, setSelectedDeployment] = useState<string | null>(mockDeployments[0]?.id || null);
  
  // Get deployment details by ID
  const getDeploymentDetails = (id: string) => {
    return mockDeployments.find(deployment => deployment.id === id);
  };
  
  // Function to get status details based on deployment status
  const getStatusDetails = (status: string) => {
    switch(status) {
      case 'success':
        return {
          icon: <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />,
          text: 'Success',
          color: 'text-green-500',
          badge: 'bg-green-500/10 text-green-500 border-green-500/20'
        };
      case 'partial':
        return {
          icon: <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />,
          text: 'Partial',
          color: 'text-yellow-500',
          badge: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
        };
      case 'failed':
        return {
          icon: <ExclamationCircleIcon className="h-5 w-5" aria-hidden="true" />,
          text: 'Failed',
          color: 'text-red-500',
          badge: 'bg-red-500/10 text-red-500 border-red-500/20'
        };
      default:
        return {
          icon: null,
          text: 'Unknown',
          color: 'text-zinc-400',
          badge: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
        };
    }
  };
  
  // Function to format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today, ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday, ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      return diffDays + ' days ago';
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };
  
  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-950 bg-opacity-75 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-zinc-900 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                {/* Header with close button */}
                <div className="border-b border-zinc-800 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <Dialog.Title as="h3" className="text-lg font-medium text-zinc-100">
                      Deployment History
                    </Dialog.Title>
                    <button
                      type="button"
                      className="rounded-md bg-transparent text-zinc-400 hover:text-zinc-200 focus:outline-none"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                
                <div className="flex h-[28rem]">
                  {/* Sidebar with deployment list */}
                  <div className="w-1/3 overflow-y-auto border-r border-zinc-800">
                    <ul className="divide-y divide-zinc-800">
                      {mockDeployments.map((deployment) => {
                        const statusDetails = getStatusDetails(deployment.status);
                        return (
                          <li 
                            key={deployment.id}
                            className={`cursor-pointer transition-colors duration-100 hover:bg-zinc-800/50 ${
                              selectedDeployment === deployment.id ? 'bg-zinc-800' : ''
                            }`}
                            onClick={() => setSelectedDeployment(deployment.id)}
                          >
                            <div className="px-4 py-3">
                              {/* Deployment ID and Date */}
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-medium text-zinc-200">
                                  {deployment.id}
                                </div>
                                <div className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs border ${statusDetails.badge}`}>
                                  {statusDetails.text}
                                </div>
                              </div>
                              
                              {/* Date and target */}
                              <div className="mt-1 flex items-center text-xs text-zinc-400">
                                <CalendarIcon className="mr-1 h-3.5 w-3.5 flex-shrink-0 text-zinc-500" />
                                {formatDate(deployment.date)}
                              </div>
                              
                              {/* Type */}
                              <div className="mt-1 flex items-center text-xs text-zinc-400">
                                <ServerIcon className="mr-1 h-3.5 w-3.5 flex-shrink-0 text-zinc-500" />
                                {deployment.type} ({deployment.targets.length} {deployment.targets.length === 1 ? 'instance' : 'instances'})
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  
                  {/* Main content area */}
                  {selectedDeployment && (() => {
                    const deployment = getDeploymentDetails(selectedDeployment);
                    if (!deployment) return null;
                    
                    const statusDetails = getStatusDetails(deployment.status);
                    
                    return (
                      <div className="flex-1 overflow-y-auto p-6">
                        {/* Deployment header */}
                        <div className="flex items-center justify-between mb-6">
                          <h4 className="text-lg font-medium text-zinc-100">
                            Deployment {deployment.id}
                          </h4>
                          <div className={`flex items-center ${statusDetails.color}`}>
                            {statusDetails.icon}
                            <span className="ml-1.5 text-sm font-medium">{statusDetails.text}</span>
                          </div>
                        </div>
                        
                        {/* Deployment details */}
                        <div className="space-y-6">
                          {/* Basic information */}
                          <div className="bg-zinc-800/50 rounded-lg overflow-hidden">
                            <div className="px-4 py-5 sm:p-6">
                              <h5 className="text-sm font-medium text-zinc-300 mb-4">Details</h5>
                              <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                                <div className="col-span-1">
                                  <dt className="text-xs text-zinc-500">Deployment Type</dt>
                                  <dd className="mt-1 text-sm text-zinc-300">{deployment.type}</dd>
                                </div>
                                <div className="col-span-1">
                                  <dt className="text-xs text-zinc-500">Initiated By</dt>
                                  <dd className="mt-1 text-sm text-zinc-300">{deployment.user}</dd>
                                </div>
                                <div className="col-span-1">
                                  <dt className="text-xs text-zinc-500">Date & Time</dt>
                                  <dd className="mt-1 text-sm text-zinc-300">{deployment.date}</dd>
                                </div>
                                <div className="col-span-1">
                                  <dt className="text-xs text-zinc-500">Target Count</dt>
                                  <dd className="mt-1 text-sm text-zinc-300">{deployment.targets.length} {deployment.targets.length === 1 ? 'instance' : 'instances'}</dd>
                                </div>
                              </dl>
                            </div>
                          </div>
                          
                          {/* Target instances */}
                          <div className="bg-zinc-800/50 rounded-lg overflow-hidden">
                            <div className="px-4 py-5 sm:p-6">
                              <h5 className="text-sm font-medium text-zinc-300 mb-4">Target Instances</h5>
                              <ul className="divide-y divide-zinc-700/50">
                                {deployment.targets.map((target, index) => (
                                  <li key={index} className="py-3 flex items-center justify-between">
                                    <div className="flex items-center">
                                      <ServerIcon className="h-4 w-4 text-zinc-500 mr-2" />
                                      <span className="text-sm text-zinc-300">{target}</span>
                                    </div>
                                    <div className={`${statusDetails.color}`}>
                                      {statusDetails.icon}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex justify-end space-x-3">
                            <button 
                              className="inline-flex items-center px-3 py-2 border border-zinc-700 rounded-md shadow-sm text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700"
                            >
                              <DocumentTextIcon className="h-4 w-4 mr-1.5" />
                              View Logs
                            </button>
                            
                            {(deployment.status === 'failed' || deployment.status === 'partial') && (
                              <button 
                                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                              >
                                <ArrowPathIcon className="h-4 w-4 mr-1.5" />
                                Retry Deployment
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default DeploymentHistoryModal; 